import React from 'react';
import { Box, Typography, TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Grid, Button, ToggleButtonGroup, ToggleButton, Chip, Tooltip } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import SettingsIcon from '@mui/icons-material/Settings';
import { format, parseISO } from 'date-fns';
import { pt } from 'date-fns/locale';
import { HomeExpenseDto, HomeExpenseGroupDto } from '../../../types/homeExpenses';
import { getGroupOrdering, upsertBulkGroupOrdering } from '../../../services/homeExpenseGroupOrderingApi';
import { useExpensesYear } from '../../../context/ExpensesYearContext';

interface Props {
  expenses: HomeExpenseDto[];
  groups: HomeExpenseGroupDto[];
  formatCurrency: (n: number) => string;
}

const monthKey = (dateStr: string) => {
  const d = parseISO(dateStr);
  return format(d, 'yyyy-MM');
};
const monthLabel = (key: string) => {
  const [y, m] = key.split('-');
  const date = new Date(Number(y), Number(m) - 1, 1);
  return format(date, 'MMM', { locale: pt });
};

const EnhancedMonthlyPivot: React.FC<Props> = ({ expenses, groups, formatCurrency }) => {
  const { year } = useExpensesYear();
  const [groupOrderDialogOpen, setGroupOrderDialogOpen] = React.useState(false);
  const [orderingMode, setOrderingMode] = React.useState(false);

  const [groupPins, setGroupPins] = React.useState<Record<string, 'start' | 'end'>>(() => {
    try { const raw = localStorage.getItem('pivotGroupPins'); if (raw) return JSON.parse(raw); } catch { }
    return {};
  });
  React.useEffect(() => { try { localStorage.setItem('pivotGroupPins', JSON.stringify(groupPins)); } catch { } }, [groupPins]);
  const updatePin = (groupId: string, value: 'start' | 'end' | null) => {
    setGroupPins(prev => { const next = { ...prev }; if (!value) delete next[groupId]; else next[groupId] = value; return next; });
  };

  const [groupOrdering, setGroupOrdering] = React.useState<Record<string, string[]>>(() => {
    try { const raw = localStorage.getItem('pivotGroupOrdering'); if (raw) return JSON.parse(raw); } catch { }
    return {};
  });
  const [orderingLoaded, setOrderingLoaded] = React.useState(false);
  const pendingSaveRef = React.useRef<NodeJS.Timeout | null>(null);
  const dirtyRef = React.useRef(false);
  const mergeOrdering = React.useCallback((backend: Record<string,string[]>, local: Record<string,string[]>) => {
    const result: Record<string,string[]> = { ...backend };
    Object.keys(local).forEach(cat => {
      if (!result[cat]) result[cat] = local[cat]; else {
        const set = new Set(result[cat]);
        local[cat].forEach(id => { if (!set.has(id)) result[cat].push(id); });
      }
    });
    return result;
  }, []);
  React.useEffect(() => { let cancelled = false; (async () => { try { const remote = await getGroupOrdering(); if (cancelled) return; const backendMap: Record<string,string[]> = {}; remote.forEach(r => { backendMap[r.categoryName] = r.ordering; }); const merged = mergeOrdering(backendMap, groupOrdering); setGroupOrdering(merged); } catch { } finally { if (!cancelled) setOrderingLoaded(true); } })(); return () => { cancelled = true; }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React.useEffect(() => {
    try { localStorage.setItem('pivotGroupOrdering', JSON.stringify(groupOrdering)); } catch { }
    if (!orderingLoaded) return; dirtyRef.current = true; if (pendingSaveRef.current) clearTimeout(pendingSaveRef.current);
    pendingSaveRef.current = setTimeout(async () => { if (!dirtyRef.current) return; dirtyRef.current = false; try { const payload = Object.keys(groupOrdering).map(cat => ({ categoryName: cat, ordering: groupOrdering[cat] })); await upsertBulkGroupOrdering(payload); } catch { dirtyRef.current = true; } }, 750);
  }, [groupOrdering, orderingLoaded]);

  const applyManualOrdering = (categoryName: string, groupsInCat: HomeExpenseGroupDto[]) => {
    const order = groupOrdering[categoryName]; if (!order || !order.length) return groupsInCat; const map = new Map(order.map((id, idx) => [id, idx])); const covered = groupsInCat.filter(g => map.has(g.publicId)); if (covered.length < groupsInCat.length * 0.5) return groupsInCat; return [...groupsInCat].sort((a,b) => { const ai = map.has(a.publicId) ? map.get(a.publicId)! : 9999; const bi = map.has(b.publicId) ? map.get(b.publicId)! : 9999; return ai - bi || a.name.localeCompare(b.name); });
  };
  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, category: string, gid: string) => { e.dataTransfer.setData('text/plain', JSON.stringify({ category, gid })); };
  const handleDragOver = (e: React.DragEvent) => { if (orderingMode) e.preventDefault(); };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, category: string, targetGid: string) => { if (!orderingMode) return; e.preventDefault(); try { const data = JSON.parse(e.dataTransfer.getData('text/plain')) as { category: string; gid: string }; if (data.category !== category || data.gid === targetGid) return; setGroupOrdering(prev => { const existing = prev[category] ? [...prev[category]] : []; if (!existing.length) { const catGroups = groups.filter(g => (g.category?.name || 'Uncategorized') === category); existing.push(...catGroups.map(g => g.publicId)); } const fromIdx = existing.indexOf(data.gid); const toIdx = existing.indexOf(targetGid); if (fromIdx === -1 || toIdx === -1) return prev; existing.splice(fromIdx, 1); existing.splice(toIdx, 0, data.gid); return { ...prev, [category]: existing }; }); } catch { } };
  const clearOrdering = () => { setGroupOrdering({}); };

  const yearFilteredExpenses = React.useMemo(() => expenses.filter(e => new Date(e.date).getFullYear() === year), [expenses, year]);

  // Active groups (at least one transaction in selected year)
  const activeGroupIds = React.useMemo(() => {
    const set = new Set<string>();
    yearFilteredExpenses.forEach(e => {
      const g = groups.find(g => g.name === e.group);
      if (g) set.add(g.publicId);
    });
    return set;
  }, [yearFilteredExpenses, groups]);
  const categoryOrder = React.useMemo(() => { const map = new Map<string, { name: string; sortOrder: number; color?: string }>(); groups.forEach(g => { if (g.category && !map.has(g.category.publicId)) { map.set(g.category.publicId, { name: g.category.name, sortOrder: g.category.sortOrder, color: g.category.color }); } }); return Array.from(map.values()).sort((a,b)=> a.sortOrder === b.sortOrder ? a.name.localeCompare(b.name) : a.sortOrder - b.sortOrder); }, [groups]);
  const groupsByCategory = React.useMemo(() => {
    const result: { category: string; groups: HomeExpenseGroupDto[] }[] = [];
    categoryOrder.forEach(cat => {
      const baseAll = groups.filter(g => g.category?.name === cat.name).sort((a,b)=> a.name.localeCompare(b.name));
      const base = baseAll.filter(g => activeGroupIds.has(g.publicId));
      if (!base.length) return; // skip category with no active groups
      let ordered = base;
      const manual = applyManualOrdering(cat.name, base);
      if (manual !== base) {
        ordered = manual;
      } else {
        const start = base.filter(g => groupPins[g.publicId] === 'start');
        const end = base.filter(g => groupPins[g.publicId] === 'end');
        const middle = base.filter(g => !groupPins[g.publicId]);
        ordered = [...start, ...middle, ...end];
      }
      result.push({ category: cat.name, groups: ordered });
    });
    const uncategorizedAll = groups.filter(g => !g.category).sort((a,b)=> a.name.localeCompare(b.name));
    const uncategorized = uncategorizedAll.filter(g => activeGroupIds.has(g.publicId));
    if (uncategorized.length) result.push({ category: 'Uncategorized', groups: uncategorized });
    return result;
  }, [groups, categoryOrder, groupPins, groupOrdering, activeGroupIds]);
  const months = React.useMemo(() => { const arr: string[] = []; for (let m = 0; m < 12; m++) { const key = `${year}-${String(m+1).padStart(2,'0')}`; arr.push(key); } return arr; }, [year]);
  const monthGroupSums = React.useMemo(() => { const sums: Record<string, Record<string, number>> = {}; yearFilteredExpenses.forEach(e => { const m = monthKey(e.date); const groupObj = groups.find(g => g.name === e.group); if (!groupObj) return; if (!sums[m]) sums[m] = {}; const gid = groupObj.publicId; sums[m][gid] = (sums[m][gid] || 0) + e.amount * (e.isIncome ? -1 : 1); }); return sums; }, [yearFilteredExpenses, groups]);
  // Removed Income/Expense/Net summary table computation (feature dropped)

  // Highlighted cells (ctrl+click)
  const [highlightedCells, setHighlightedCells] = React.useState<Set<string>>(new Set());
  const toggleHighlight = (key: string) => {
    setHighlightedCells(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <Tooltip title="Exportar CSV">
          <Button size="small" variant="outlined" onClick={()=> {
            try {
              const headers: string[] = ['Mês'];
              const groupColumns = groupsByCategory.flatMap(sec => sec.groups.map(g => g.name));
              headers.push(...groupColumns);
              const rows = months.map(m => {
                const sums = monthGroupSums[m] || {};
                const formatted = groupColumns.map(name => {
                  const g = groups.find(gr => gr.name === name);
                  if (!g) return '';
                  const valEntry = Object.entries(sums).find(([gid]) => gid === g.publicId);
                  if (!valEntry) return '';
                  const val = valEntry[1];
                  return (val < 0 ? -val : val).toString();
                });
                return [monthLabel(m), ...formatted];
              });
              const csv = [headers, ...rows].map(r => r.map(field => `"${field.replace(/"/g,'""')}"`).join(',')).join('\n');
              const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url; a.download = `pivot-${year}.csv`; a.click(); URL.revokeObjectURL(url);
            } catch {}
          }}>CSV</Button>
        </Tooltip>
        <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
          <IconButton size="small" onClick={()=> setOrderingMode(m => !m)} aria-label="alternar modo de ordenação" color={orderingMode ? 'primary' : 'default'}>
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={()=> setGroupOrderDialogOpen(true)} aria-label="configurar ordem dos grupos">
            <SettingsIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader sx={{ tableLayout: 'fixed', '& td, & th': { borderRight: '1px solid', borderColor: 'divider' }, '& td:last-of-type, & th:last-of-type': { borderRight: 'none' } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, background: 'background.paper', width: 54, p:0.5, fontSize:11 }}>Mês</TableCell>
              {groupsByCategory.map(section => (
                <TableCell key={section.category} align="center" colSpan={section.groups.length} sx={{ background: 'background.paper', borderLeft: '1px solid', borderColor: 'divider', p: 0 }}>
                  <Box sx={{ fontWeight: 600, fontSize: 12 }}>{section.category === 'Uncategorized' ? 'Sem categoria' : section.category}</Box>
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell sx={{ fontSize: 11 }}> </TableCell>
              {groupsByCategory.flatMap(section => section.groups.map(g => (
                <TableCell key={g.publicId} sx={{ fontSize: 10, background: 'background.paper', p: 0.5, width: 90 }}>
                  <Box sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.1 }}>{g.name}</Box>
                </TableCell>
              )))}
            </TableRow>
          </TableHead>
          <TableBody>
            {months.map(m => {
              const sums = monthGroupSums[m] || {};
              return (
                <TableRow key={m} hover>
                  <TableCell sx={{ fontWeight: 500, p:0.5, fontSize:11 }}>{monthLabel(m)}</TableCell>
                  {groupsByCategory.flatMap(section => section.groups.map(g => {
                    const val = sums[g.publicId] || 0; const isNeg = val < 0; const categoryColor = g.category?.color || '#e0e0e0'; const bg = categoryColor + (categoryColor.length === 7 ? '22' : '');
                    const cellKey = `${m}:${g.publicId}`;
                    const highlighted = highlightedCells.has(cellKey);
                    return (
                      <TableCell
                        key={g.publicId}
                        onClick={(e)=> { if (e.ctrlKey) toggleHighlight(cellKey); }}
                        sx={{ p: 0.5, fontSize: 11, color: isNeg ? 'success.dark' : 'error.dark', backgroundColor: bg, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: highlighted ? 700 : 400, cursor: 'pointer' }}
                        title={val === 0 ? '' : (isNeg ? `Receita: ${formatCurrency(Math.abs(val))}` : `Despesa: ${formatCurrency(val)}`)}
                      >
                        {val === 0 ? '' : formatCurrency(Math.abs(val))}
                      </TableCell>
                    );
                  }))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
        Rendimentos (verde) subtraídos. Valores a vermelho representam despesas. Ctrl+Click para destacar células.
      </Typography>
      <Dialog open={groupOrderDialogOpen} onClose={()=> setGroupOrderDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Configurar ordem dos grupos (arrastar e fixar)</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Pode arrastar grupos (pega à esquerda) para definir a ordem manual. Os grupos fixados (Início/Fim) só se aplicam quando não existe ordem manual para a categoria.
          </Typography>
          <Grid container spacing={2}>
            {groupsByCategory.map(section => (
              <Grid size={{ xs: 12 }} key={section.category}>
                <Box sx={{ fontWeight: 600, mb: 1, mt: 1 }}>{section.category}</Box>
                {section.groups.map(g => { const pin = groupPins[g.publicId] || null; return (
                  <Box key={g.publicId} draggable={orderingMode} onDragStart={(e)=> handleDragStart(e, section.category, g.publicId)} onDragOver={handleDragOver} onDrop={(e)=> handleDrop(e, section.category, g.publicId)} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, pl: 1, pr: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: orderingMode ? 'grab' : 'default', opacity: orderingMode ? 0.85 : 1 }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <DragIndicatorIcon fontSize="small" sx={{ visibility: orderingMode ? 'visible' : 'hidden', color: 'text.secondary' }} />
                      <Box sx={{ width:10, height:10, borderRadius:'50%', backgroundColor: g.category?.color || '#9e9e9e' }} />
                      <Typography variant="body2" sx={{ fontWeight: pin ? 600 : 400 }}>{g.name}</Typography>
                    </Box>
                    <ToggleButtonGroup exclusive size="small" value={pin} onChange={(_, val) => updatePin(g.publicId, val)}>
                      <ToggleButton value={null as any} sx={{ px:1 }} selected={pin===null}>Normal</ToggleButton>
                      <ToggleButton value="start" sx={{ px:1 }}>Início</ToggleButton>
                      <ToggleButton value="end" sx={{ px:1 }}>Fim</ToggleButton>
                    </ToggleButtonGroup>
                  </Box> ); })}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearOrdering} color="warning" variant="outlined">Limpar ordem</Button>
          <Button onClick={()=> { setGroupPins({}); }} color="warning">Limpar fixados</Button>
          <Button onClick={()=> setGroupOrderDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EnhancedMonthlyPivot;